import type { ReflectionNote } from "@couples/shared";
import { useState } from "react";

import { formatDateJa } from "../lib/dates";
import SheetShell from "./SheetShell";

interface CreateInput {
  title: string;
  occurredOn: string;
  cause: string;
  nextStep: string;
}

interface AddEntryInput {
  noteId: string;
  cause: string;
  nextStep: string;
}

interface Props {
  /** 既存のノートに自分の振り返りを追記するときだけ渡す。無ければ新規作成 */
  existingNote?: ReflectionNote;
  partnerName: string;
  todayKey: string;
  busy: boolean;
  onCreate: (input: CreateInput) => void;
  onAddEntry: (input: AddEntryInput) => void;
  onClose: () => void;
}

/** 「きっかけ」「いつ」「自分の振り返り」を書く下シート。新規作成と既存ノートへの追記を兼ねる */
export default function ReflectionEntrySheet({
  existingNote,
  partnerName,
  todayKey,
  busy,
  onCreate,
  onAddEntry,
  onClose,
}: Props) {
  const [title, setTitle] = useState("");
  const [occurredOn, setOccurredOn] = useState(todayKey);
  const [cause, setCause] = useState("");
  const [nextStep, setNextStep] = useState("");

  const causeText = cause.trim();
  const nextStepText = nextStep.trim();
  const titleText = title.trim();
  const canSubmit =
    causeText !== "" && nextStepText !== "" && (existingNote !== undefined || titleText !== "");

  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;
    if (existingNote !== undefined) {
      onAddEntry({ noteId: existingNote.id, cause: causeText, nextStep: nextStepText });
    } else {
      onCreate({ title: titleText, occurredOn, cause: causeText, nextStep: nextStepText });
    }
  }

  return (
    <SheetShell title="ふりかえりを書く" onClose={onClose} tone="bg">
      <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
        <div className="flex flex-1 flex-col gap-3.5 overflow-y-auto p-4">
          {existingNote === undefined ? (
            <>
              <label className="block">
                <span className="mb-1 block text-[11px] text-[var(--color-sub)]">きっかけ</span>
                <input
                  type="text"
                  value={title}
                  onChange={(event) => {
                    setTitle(event.target.value);
                  }}
                  placeholder="何があったか(短く)"
                  className="w-full rounded-lg border border-[var(--color-line)] bg-white px-2.5 py-2 text-base outline-none"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-[11px] text-[var(--color-sub)]">いつ</span>
                <input
                  type="date"
                  value={occurredOn}
                  max={todayKey}
                  onChange={(event) => {
                    setOccurredOn(event.target.value);
                  }}
                  className="w-full rounded-lg border border-[var(--color-line)] bg-white px-2.5 py-2 text-base outline-none"
                />
              </label>
            </>
          ) : (
            <div className="rounded-lg bg-white p-3">
              <span className="diary-stamp">{formatDateJa(existingNote.occurredOn)}</span>
              <p className="mt-1.5 text-sm font-bold">{existingNote.title}</p>
            </div>
          )}

          <div className="rounded-xl border border-[var(--color-line)] bg-white p-3">
            <span className="mb-1.5 block text-[11px] font-semibold text-[var(--color-sub)]">
              自分の振り返り
            </span>
            <label className="block">
              <span className="mb-1 block text-[11px] text-[var(--color-sub)]">
                原因について思うこと
              </span>
              <textarea
                value={cause}
                onChange={(event) => {
                  setCause(event.target.value);
                }}
                placeholder="何が起きたか、自分から見てどう感じたか"
                rows={3}
                className="w-full resize-none rounded-lg border border-[var(--color-line)] bg-white px-2.5 py-2 text-base outline-none"
              />
            </label>
            <label className="mt-2 block">
              <span className="mb-1 block text-[11px] text-[var(--color-sub)]">
                次に気をつけたいこと
              </span>
              <textarea
                value={nextStep}
                onChange={(event) => {
                  setNextStep(event.target.value);
                }}
                placeholder="次はどうしたいか"
                rows={2}
                className="w-full resize-none rounded-lg border border-[var(--color-line)] bg-white px-2.5 py-2 text-base outline-none"
              />
            </label>
          </div>

          <p className="text-[11.5px] leading-relaxed text-[var(--color-sub)]">
            書いたあと、{partnerName}さんが自分の振り返りを追記できます。二人分そろったら、
            話し合ってから「解決済み」にできます。
          </p>
        </div>

        <div className="flex gap-2.5 border-t border-[var(--color-line)] p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
          <button
            type="submit"
            disabled={busy || !canSubmit}
            className="flex-1 rounded-xl bg-[var(--color-teal)] py-2.5 text-sm font-semibold text-white disabled:opacity-50"
          >
            書く
          </button>
        </div>
      </form>
    </SheetShell>
  );
}
